/** Staged construction art. All geometry stays registered to the finished
 * building's authored ground plane; progress only reads the simulation. */
import { Graphics, type Container, type Sprite } from 'pixi.js';
import { mix, shade } from '../data/palette.ts';
import type { BuildingGeometry } from './textures.ts';
import type { Vernacular } from './vernacular.ts';

type Point = [number, number];
export type ConstructionStage = 'foundations' | 'frame' | 'walls' | 'roof' | 'complete';
const STAGES = ['foundations', 'frame', 'walls', 'roof', 'complete'] as const;
const BREAKS = [0, .18, .48, .76, 1];

function stagePhase(build: number, stage: ConstructionStage): number {
  const index = STAGES.indexOf(stage);
  if (index === 4) return 1;
  const p = Number.isFinite(build) ? Math.max(0, Math.min(1, build)) : 0;
  return Math.floor(Math.max(0, Math.min(1,
    (p - BREAKS[index]) / (BREAKS[index + 1] - BREAKS[index]))) * 16) / 16;
}

export function constructionStage(build: number): ConstructionStage {
  const p = Number.isFinite(build) ? Math.max(0, Math.min(1, build)) : 0;
  return STAGES[p >= 1 ? 4 : p >= .76 ? 3 : p >= .48 ? 2 : p >= .18 ? 1 : 0];
}

export interface ConstructionProfile {
  stage: ConstructionStage;
  /** A stable redraw key: sixteen steps in each stage, never wall-clock time. */
  key: string;
  phase: number;
  /** Coordinates are relative to the exact sprite ground anchor. */
  footing: Point[];
  frame: Point[];
  height: number;
  postHeight: number;
  revealY: number;
  bounds: { x0: number; y0: number; x1: number; y1: number };
}

export function constructionProfile(geometry: BuildingGeometry, build: number): ConstructionProfile {
  const stage = constructionStage(build), phase = stagePhase(build, stage);
  const footing = geometry.groundFootprint.map(([x, y]): Point =>
    [x - geometry.groundCenterX, y - geometry.groundFrontY]);
  // The four extremal feet inherit the asset's actual projected axes. An
  // independent screen-space rectangle would make the framing stand crooked.
  const left = footing.reduce((a, b) => a[0] < b[0] ? a : b);
  const back = footing.reduce((a, b) => a[1] < b[1] ? a : b);
  const right = footing.reduce((a, b) => a[0] > b[0] ? a : b);
  const front = footing.reduce((a, b) => a[1] > b[1] ? a : b);
  const cy = (back[1] + front[1]) / 2;
  const frame = [left, back, right, front].map(([x, y]): Point => [x * .89, cy + (y - cy) * .89]);
  const bounds = {
    x0: geometry.bounds.x0 - geometry.groundCenterX,
    y0: geometry.bounds.y0 - geometry.groundFrontY,
    x1: geometry.bounds.x1 - geometry.groundCenterX,
    y1: geometry.bounds.y1 - geometry.groundFrontY,
  };
  const rise = Math.max(12, cy - bounds.y0);
  const height = Math.max(9, rise * .61);
  const postHeight = stage === 'foundations' ? 3 + phase * 3 :
    stage === 'frame' ? height * (.25 + phase * .75) : height;
  // Lower walls are uncovered first. The roof line is deliberately held until
  // the last stage, instead of tinting a complete building at every progress.
  const eaveY = back[1] - height * .24;
  const revealY = stage === 'walls' ? bounds.y1 + (eaveY - bounds.y1) * (.12 + phase * .88) :
    stage === 'roof' ? eaveY + (bounds.y0 - 2 - eaveY) * phase :
      stage === 'complete' ? bounds.y0 - 2 : bounds.y1 + 2;
  return { stage, key: `${stage}:${phase}`, phase, footing, frame, height, postHeight, revealY, bounds };
}

function convexHull(points: Point[]): Point[] {
  const sorted = points.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (a: Point, b: Point, c: Point) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  const half = (list: Point[]) => {
    const out: Point[] = [];
    for (const point of list) {
      while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], point) <= 0) out.pop();
      out.push(point);
    }
    return out;
  };
  return [...half(sorted).slice(0, -1), ...half(sorted.reverse()).slice(0, -1)];
}

/** Match the horizontal sprite mask, including where roof edges cross it.
 * Filtering vertices alone discards visible eaves between their endpoints. */
function belowCut(points: Point[], cut: number): Point[] {
  const clipped: Point[] = [];
  let previous = points[points.length - 1];
  for (const current of points) {
    const fromInside = previous[1] >= cut, toInside = current[1] >= cut;
    if (fromInside !== toInside) {
      const t = (cut - previous[1]) / (current[1] - previous[1]);
      clipped.push([previous[0] + (current[0] - previous[0]) * t, cut]);
    }
    if (toInside) clipped.push(current);
    previous = current;
  }
  return clipped;
}

/** Picking follows the built site; an invisible future roof cannot intercept
 * a click on workers or ground behind the foundations. Authored coordinates. */
export function constructionSilhouette(geometry: BuildingGeometry, build: number): Point[] {
  const profile = constructionProfile(geometry, build);
  if (profile.stage === 'complete') return geometry.silhouette;
  const points = [...profile.footing, ...profile.frame.map(([x, y]): Point => [x, y - profile.postHeight])];
  if (profile.stage === 'walls' || profile.stage === 'roof') {
    const silhouette = geometry.silhouette.map(([x, y]): Point =>
      [x - geometry.groundCenterX, y - geometry.groundFrontY]);
    points.push(...belowCut(silhouette, profile.revealY));
  }
  return convexHull(points).map(([x, y]): Point => [x + geometry.groundCenterX, y + geometry.groundFrontY]);
}

function line(g: Graphics, a: Point, b: Point, color: number, width = 1, alpha = 1): void {
  g.moveTo(a[0], a[1]).lineTo(b[0], b[1]).stroke({ color, width, alpha, cap: 'round' });
}
const up = ([x, y]: Point, height: number): Point => [x, y - height];
const between = (a: Point, b: Point, t: number): Point => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

function timber(g: Graphics, a: Point, b: Point, color: number, width = 2.2): void {
  line(g, a, b, shade(color, .57), width + 1.2);
  line(g, a, b, color, width);
  line(g, [a[0] - .35, a[1] - .45], [b[0] - .35, b[1] - .45], mix(color, 0xd8b981, .36), .6);
}

function drawSite(back: Graphics, front: Graphics, mask: Graphics, p: ConstructionProfile, style: Vernacular): void {
  back.clear(); front.clear(); mask.clear();
  const wood = mix(style.timber, 0xa47b46, .52), stone = mix(style.wallLit, 0xaaa08a, .5);
  const baseHeight = p.stage === 'foundations' ? p.postHeight : 5.5;
  const middleY = p.footing.reduce((sum, point) => sum + point[1], 0) / p.footing.length;
  back.poly(p.footing.flat()).fill({ color: 0x302d24, alpha: .48 });
  const top = p.footing.map(point => up(point, baseHeight));
  // A shallow stone footing with visible front faces makes contact legible.
  for (let i = 0; i < p.footing.length; i++) {
    const a = p.footing[i], b = p.footing[(i + 1) % p.footing.length];
    const near = (a[1] + b[1]) / 2 > middleY;
    const layer = near ? front : back;
    layer.poly([a, b, up(b, baseHeight), up(a, baseHeight)].flat())
      .fill({ color: shade(stone, near ? .69 : .8), alpha: p.stage === 'roof' ? .55 : .95 });
    line(layer, up(a, baseHeight), up(b, baseHeight), stone, 2.1);
    const blocks = Math.max(2, Math.min(8, Math.round(Math.hypot(b[0] - a[0], b[1] - a[1]) / 7)));
    for (let j = 1; j < blocks; j++) {
      const foot = between(a, b, j / blocks);
      line(layer, foot, up(foot, baseHeight - .2), shade(stone, .46), .65, .72);
    }
  }
  back.poly(top.flat()).fill({ color: 0x6c5b42, alpha: .37 });

  // Stacked beams and cut stone sit inside the reserved lot, never in a road.
  const stack = between(p.frame[0], p.frame[3], .52);
  const span = between(p.frame[1], p.frame[2], .3);
  const dx = (p.frame[2][0] - p.frame[1][0]) * .27;
  const dy = (p.frame[2][1] - p.frame[1][1]) * .27;
  for (let i = 0; i < (p.stage === 'roof' ? 2 : 4); i++) {
    const a: Point = [stack[0] + i * .4, stack[1] - 1.2 - i * 1.8];
    timber(front, a, [a[0] + dx, a[1] + dy], wood, 2);
  }
  for (let i = 0; i < 3; i++) {
    const x = span[0] + i * 3.2, y = span[1] - 2;
    back.poly([x, y, x + 3, y - 1.5, x + 6, y, x + 3, y + 1.5]).fill(stone);
    back.poly([x, y, x + 3, y + 1.5, x + 3, y + 4, x, y + 2.5]).fill(shade(stone, .66));
  }
  if (p.stage === 'foundations') {
    // Low setting-out pegs remain honest to the foundation's small height.
    for (const foot of p.frame) timber(front, foot, up(foot, 5), wood, 1.2);
    return;
  }

  const scaffoldAlpha = p.stage === 'roof' ? 1 - p.phase * .6 : 1;
  back.alpha = 1; front.alpha = scaffoldAlpha;
  for (let i = 0; i < p.frame.length; i++) {
    const a = p.frame[i], b = p.frame[(i + 1) % p.frame.length];
    const near = i >= 2, g = near ? front : back;
    const height = p.postHeight;
    timber(g, up(a, baseHeight), up(a, height), wood);
    timber(g, up(a, height), up(b, height), wood);
    const middle = between(a, b, .5);
    if (Math.hypot(b[0] - a[0], b[1] - a[1]) > 26) timber(g, up(middle, baseHeight), up(middle, height), wood, 1.7);
    // Bracing and a working platform turn thin posts into a substantial frame.
    if (height > 14) {
      timber(g, up(a, height * .22), up(b, height * .76), wood, 1.3);
      timber(g, up(a, height * .58), up(b, height * .58), wood, 2.4);
    }
  }
  // A short ladder follows the near face, at worker scale, rather than an
  // arbitrary vertical stripe through the middle of the finished doorway.
  const foot = between(p.frame[2], p.frame[3], .33), ladderHeight = Math.min(p.postHeight * .66, 27);
  const ladderTop: Point = [foot[0] - 3, foot[1] - ladderHeight];
  const rung: Point = [-3.3, 1.65];
  for (const offset of [0, 1]) timber(front,
    [foot[0] + rung[0] * offset, foot[1] + rung[1] * offset],
    [ladderTop[0] + rung[0] * offset, ladderTop[1] + rung[1] * offset], wood, .9);
  for (let t = .14; t < 1; t += 3.5 / ladderHeight) {
    const a = between(foot, ladderTop, t);
    line(front, a, [a[0] + rung[0], a[1] + rung[1]], mix(wood, 0xdbbd85, .35), 1.1);
  }
  // Roofing reveals the asset's own orientation and crown. The perimeter
  // scaffold supports the work without imposing a gable on domes or towers.
  // The mask's full width preserves porches, wings and faction-specific eaves.
  if (p.stage === 'walls' || p.stage === 'roof') mask.rect(p.bounds.x0 - 2, p.revealY,
    p.bounds.x1 - p.bounds.x0 + 4, Math.max(1, p.bounds.y1 + 3 - p.revealY)).fill(0xffffff);
}

interface Site {
  back: Graphics;
  front: Graphics;
  mask: Graphics;
  sprite: Sprite | null;
  seen: number;
  geometry: BuildingGeometry | null;
  texture: object | null;
  style: Vernacular | null;
  key: string;
}

/** A bounded pool: at most the largest set of simultaneously visible sites.
 * begin/end remove stale masks when a building completes, is culled or when a
 * different city is opened. Moving the camera only changes transforms. */
export class ConstructionSites {
  private readonly active = new Map<number, Site>();
  private readonly free: Site[] = [];
  private world: object | null = null;
  private epoch = 0;

  constructor(private readonly parent: Container) {}

  begin(world: object): void {
    if (world !== this.world) {
      for (const site of this.active.values()) this.retire(site);
      this.active.clear(); this.world = world;
    }
    this.epoch++;
  }

  apply(index: number, sprite: Sprite, geometry: BuildingGeometry, build: number, style: Vernacular): void {
    const stage = constructionStage(build);
    if (stage === 'complete') {
      const old = this.active.get(index);
      if (old) { this.retire(old); this.active.delete(index); }
      sprite.renderable = true;
      return;
    }
    let site = this.active.get(index);
    if (!site) {
      site = this.free.pop() ?? this.create();
      this.active.set(index, site);
    }
    if (site.sprite && site.sprite !== sprite) {
      site.sprite.mask = null; site.sprite.renderable = true;
    }
    site.sprite = sprite; site.seen = this.epoch;
    const key = `${stage}:${stagePhase(build, stage)}`;
    if (site.key !== key || site.geometry !== geometry || site.texture !== sprite.texture || site.style !== style) {
      const profile = constructionProfile(geometry, build);
      site.back.alpha = 1; site.front.alpha = 1;
      drawSite(site.back, site.front, site.mask, profile, style);
      site.key = key; site.geometry = geometry; site.texture = sprite.texture; site.style = style;
    }
    for (const part of [site.back, site.front, site.mask]) {
      part.visible = true;
      part.position.copyFrom(sprite.position); part.scale.copyFrom(sprite.scale);
      part.rotation = sprite.rotation;
    }
    site.back.zIndex = sprite.zIndex - .05; site.front.zIndex = sprite.zIndex + .05;
    site.mask.zIndex = sprite.zIndex;
    const reveal = stage === 'walls' || stage === 'roof';
    sprite.renderable = reveal; sprite.alpha = 1; sprite.tint = 0xffffff;
    sprite.mask = reveal ? site.mask : null;
    // A Graphics mask should never draw as a white rectangle in early stages.
    site.mask.visible = reveal;
  }

  end(): void {
    for (const [index, site] of this.active) if (site.seen !== this.epoch) {
      this.retire(site); this.active.delete(index);
    }
  }

  destroy(): void {
    for (const site of this.active.values()) this.retire(site);
    this.active.clear();
    for (const site of this.free) {
      site.back.destroy(); site.front.destroy(); site.mask.destroy();
    }
    this.free.length = 0; this.world = null;
  }

  private create(): Site {
    const back = new Graphics(), front = new Graphics(), mask = new Graphics();
    for (const g of [back, front, mask]) { g.eventMode = 'none'; this.parent.addChild(g); }
    return { back, front, mask, sprite: null, seen: -1, geometry: null, texture: null, style: null, key: '' };
  }

  private retire(site: Site): void {
    if (site.sprite) { site.sprite.mask = null; site.sprite.renderable = true; }
    site.sprite = null;
    site.back.visible = false; site.front.visible = false; site.mask.visible = false;
    this.free.push(site);
  }
}
